import React, { useState, useCallback, useMemo } from "react";
import {
  WIDGET_TYPE_OPTIONS,
  COLOR_OPTIONS,
  ICON_OPTIONS,
  getSchemaForType,
  getDefaultOptions,
  getDefaultItem,
  getDefaultColumn,
  getDefaultReferenceLine,
  getDefaultColorRule,
} from "./widgetFormSchemas";

export default function WidgetEditor({ widget, widgetIndex, onChange, onRemove, dragHandleProps, dataSources }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sourceKeyPickerFor, setSourceKeyPickerFor] = useState(null);

  const editableOptions = useMemo(() => {
    return widget.options || {};
  }, [widget.options]);

  const schema = getSchemaForType(widget.type);
  const typeInfo = WIDGET_TYPE_OPTIONS.find(opt => opt.value === widget.type);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleTypeChange = useCallback((newType) => {
    onChange({
      ...widget,
      type: newType,
      options: getDefaultOptions(newType),
      items: [],
    });
  }, [widget, onChange]);

  const handleOptionChange = useCallback((key, value) => {
    const newOptions = { ...editableOptions, [key]: value };

    onChange({
      ...widget,
      options: newOptions,
    });
  }, [widget, editableOptions, onChange]);

  const handleItemChange = useCallback((itemIndex, key, value) => {
    const newItems = [...(widget.items || [])];
    newItems[itemIndex] = { ...newItems[itemIndex], [key]: value };
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  // onClick sub-object handler for items (metric_grid update action)
  const handleItemOnClickChange = useCallback((itemIndex, key, value) => {
    const newItems = [...(widget.items || [])];
    const currentOnClick = newItems[itemIndex].onClick || {};
    if (key === 'type' && !value) {
      // Clear onClick entirely when type is empty
      const { onClick, ...rest } = newItems[itemIndex];
      newItems[itemIndex] = rest;
    } else {
      newItems[itemIndex] = { ...newItems[itemIndex], onClick: { ...currentOnClick, [key]: value } };
    }
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  const handleAddItem = useCallback(() => {
    const newItems = [...(widget.items || []), getDefaultItem(widget.type)];
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  const handleRemoveItem = useCallback((itemIndex) => {
    const newItems = (widget.items || []).filter((_, idx) => idx !== itemIndex);
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  const handleColumnChange = useCallback((colIndex, key, value) => {
    const newColumns = [...(editableOptions.columns || [])];
    newColumns[colIndex] = { ...newColumns[colIndex], [key]: value };
    handleOptionChange('columns', newColumns);
  }, [editableOptions, handleOptionChange]);

  const handleAddColumn = useCallback(() => {
    const newColumns = [...(editableOptions.columns || []), getDefaultColumn()];
    handleOptionChange('columns', newColumns);
  }, [editableOptions, handleOptionChange]);

  const handleRemoveColumn = useCallback((colIndex) => {
    const newColumns = (editableOptions.columns || []).filter((_, idx) => idx !== colIndex);
    handleOptionChange('columns', newColumns);
  }, [editableOptions, handleOptionChange]);

  const handleReferenceLineChange = useCallback((lineIndex, key, value) => {
    const newLines = [...(editableOptions.referenceLines || [])];
    newLines[lineIndex] = { ...newLines[lineIndex], [key]: value };
    handleOptionChange('referenceLines', newLines);
  }, [editableOptions, handleOptionChange]);

  const handleAddReferenceLine = useCallback(() => {
    const newLines = [...(editableOptions.referenceLines || []), getDefaultReferenceLine()];
    handleOptionChange('referenceLines', newLines);
  }, [editableOptions, handleOptionChange]);

  const handleRemoveReferenceLine = useCallback((lineIndex) => {
    const newLines = (editableOptions.referenceLines || []).filter((_, idx) => idx !== lineIndex);
    handleOptionChange('referenceLines', newLines);
  }, [editableOptions, handleOptionChange]);

  // --- Color Rules handlers (for options-level colorRules, e.g. progress_bar) ---
  const handleOptionColorRuleChange = useCallback((ruleIndex, key, value) => {
    const newRules = [...(editableOptions.colorRules || [])];
    newRules[ruleIndex] = { ...newRules[ruleIndex], [key]: value };
    handleOptionChange('colorRules', newRules);
  }, [editableOptions, handleOptionChange]);

  const handleAddOptionColorRule = useCallback(() => {
    const newRules = [...(editableOptions.colorRules || []), getDefaultColorRule()];
    handleOptionChange('colorRules', newRules);
  }, [editableOptions, handleOptionChange]);

  const handleRemoveOptionColorRule = useCallback((ruleIndex) => {
    const newRules = (editableOptions.colorRules || []).filter((_, idx) => idx !== ruleIndex);
    handleOptionChange('colorRules', newRules);
  }, [editableOptions, handleOptionChange]);

  // --- Color Rules handlers (for item-level colorRules, e.g. metric_grid) ---
  const handleItemColorRuleChange = useCallback((itemIndex, ruleIndex, key, value) => {
    const newItems = [...(widget.items || [])];
    const item = { ...newItems[itemIndex] };
    const newRules = [...(item.colorRules || [])];
    newRules[ruleIndex] = { ...newRules[ruleIndex], [key]: value };
    item.colorRules = newRules;
    newItems[itemIndex] = item;
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  const handleAddItemColorRule = useCallback((itemIndex) => {
    const newItems = [...(widget.items || [])];
    const item = { ...newItems[itemIndex] };
    item.colorRules = [...(item.colorRules || []), getDefaultColorRule()];
    newItems[itemIndex] = item;
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  const handleRemoveItemColorRule = useCallback((itemIndex, ruleIndex) => {
    const newItems = [...(widget.items || [])];
    const item = { ...newItems[itemIndex] };
    item.colorRules = (item.colorRules || []).filter((_, idx) => idx !== ruleIndex);
    newItems[itemIndex] = item;
    onChange({ ...widget, items: newItems });
  }, [widget, onChange]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderField = useCallback((field, value, onFieldChange, contextData = null) => {
    // Check condition
    if (field.condition && !field.condition(contextData || editableOptions)) {
      return null;
    }

    const commonClasses = "bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 w-full focus:outline-none focus:ring-2 focus:ring-orange-500/50";

    return (
      <div key={field.key} className="space-y-1">
        <label className="text-xs font-bold text-slate-400 uppercase block">
          {field.label}
          {field.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        
         {field.type === 'text' && (
           <div className="relative">
             <div className="flex gap-1">
               <input
                 type="text"
                 className={commonClasses}
                 value={value || ''}
                 onChange={(e) => onFieldChange(field.key, e.target.value)}
                 placeholder={field.placeholder}
               />
               {field.key === 'source_key' && dataSources && dataSources.length > 0 && (
                 <button
                   type="button"
                   className="px-2.5 py-1 bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 rounded-lg transition-colors flex-shrink-0 text-xs font-bold whitespace-nowrap"
                   onClick={(e) => {
                     e.stopPropagation();
                     const pickerId = `${contextData?._itemIdx ?? 'opt'}-${field.key}`;
                     setSourceKeyPickerFor(prev => prev === pickerId ? null : pickerId);
                   }}
                   title="데이터 소스 연결"
                 >
                   🔗 연결
                 </button>
               )}
             </div>
             {field.key === 'source_key' && sourceKeyPickerFor === `${contextData?._itemIdx ?? 'opt'}-${field.key}` && (
               <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-slate-800 border border-slate-600 rounded-lg shadow-xl">
                 {dataSources.map((src, idx) => {
                   const typeBadge = {
                     column: { color: 'bg-blue-500/20 text-blue-400', label: 'DB' },
                     computed: { color: 'bg-purple-500/20 text-purple-400', label: '계산' },
                     query: { color: 'bg-emerald-500/20 text-emerald-400', label: '쿼리' },
                     api: { color: 'bg-amber-500/20 text-amber-400', label: 'API' },
                   }[src.source_type] || { color: 'bg-slate-500/20 text-slate-400', label: src.source_type };

                   return (
                     <button
                       key={idx}
                       type="button"
                       className={`w-full text-left px-3 py-2 hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-0 ${
                         value === src.source_key ? 'bg-indigo-500/10' : ''
                       }`}
                       onClick={(e) => {
                         e.stopPropagation();
                         onFieldChange(field.key, src.source_key);
                         setSourceKeyPickerFor(null);
                       }}
                     >
                       <div className="flex items-center gap-2">
                         <code className="text-xs text-indigo-400 font-mono flex-1">{src.source_key}</code>
                         <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${typeBadge.color}`}>{typeBadge.label}</span>
                       </div>
                       <div className="flex items-center gap-2 mt-0.5">
                         {src.source_name && (
                           <span className="text-[10px] text-slate-400">{src.source_name}</span>
                         )}
                         {src.target_table && !src.target_column && (
                           <span className="text-[10px] text-orange-400/70">테이블 전체 (컬럼명 필요)</span>
                         )}
                       </div>
                     </button>
                   );
                 })}
               </div>
             )}
           </div>
         )}

        {field.type === 'number' && (
          <input
            type="number"
            className={commonClasses}
            value={value ?? ''}
            onChange={(e) => onFieldChange(field.key, e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder}
          />
        )}

        {field.type === 'select' && (
          <select
            className={commonClasses}
            value={value ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              // Try to parse as number if options contain numbers
              const numVal = Number(val);
              onFieldChange(field.key, isNaN(numVal) ? val : numVal);
            }}
          >
            <option value="">선택...</option>
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {field.type === 'boolean' && (
          <button
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              value ? 'bg-orange-500' : 'bg-slate-600'
            }`}
            onClick={() => onFieldChange(field.key, !value)}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                value ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        )}

        {field.type === 'color' && (
          <div className="flex gap-1.5 flex-wrap">
            {COLOR_OPTIONS.map((colorOpt) => (
              <button
                key={colorOpt.value}
                className={`w-5 h-5 rounded-full bg-${colorOpt.value}-500 ${
                  value === colorOpt.value
                    ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900'
                    : ''
                }`}
                onClick={() => onFieldChange(field.key, colorOpt.value)}
                title={colorOpt.label}
              />
            ))}
          </div>
        )}

        {field.type === 'icon' && (
          <select className={commonClasses} value={value || ''} onChange={(e) => onFieldChange(field.key, e.target.value)}>
            <option value="">선택...</option>
            {ICON_OPTIONS.map((icon) => (
              <option key={icon} value={icon}>
                {icon}
              </option>
            ))}
          </select>
        )}

        {field.type === 'json' && (
          <textarea
            className={`${commonClasses} font-mono`}
            rows={3}
            value={value ? JSON.stringify(value, null, 2) : ''}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                onFieldChange(field.key, parsed);
              } catch {
                // Invalid JSON, ignore
              }
            }}
            placeholder={field.placeholder}
          />
        )}

        {field.helpText && (
          <p className="text-xs text-slate-500 mt-0.5">{field.helpText}</p>
        )}
      </div>
    );
  }, [editableOptions, sourceKeyPickerFor, dataSources]);

  // ============================================================================
  // COLOR RULES RENDERER
  // ============================================================================

  const renderColorRulesEditor = useCallback((rules, fields, onRuleChange, onAddRule, onRemoveRule, title = "조건부 색상") => {
    return (
      <div className="space-y-2 mt-2 pl-3 border-l-2 border-orange-500/30">
        <div className="flex items-center justify-between">
          <h5 className="text-xs font-bold text-orange-400 uppercase">{title}</h5>
          <button
            className="text-[10px] px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors"
            onClick={onAddRule}
          >
            + 규칙
          </button>
        </div>
        {(!rules || rules.length === 0) && (
          <p className="text-[10px] text-slate-500">규칙 없음 — 기본 색상이 사용됩니다</p>
        )}
        {(rules || []).map((rule, ruleIdx) => (
          <div key={ruleIdx} className="bg-slate-900/40 rounded-lg p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-500">규칙 #{ruleIdx + 1}</span>
              <button
                className="text-[10px] text-red-400 hover:text-red-300"
                onClick={() => onRemoveRule(ruleIdx)}
              >
                삭제
              </button>
            </div>
            {fields.map((field) =>
              renderField(field, rule[field.key], (key, value) => onRuleChange(ruleIdx, key, value))
            )}
          </div>
        ))}
        <p className="text-[10px] text-slate-500">위에서 아래 순으로 평가, 첫 매치 적용</p>
      </div>
    );
  }, [renderField]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!schema) return null;

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* HEADER */}
      <div
        className="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-slate-700/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Drag Handle */}
        <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Type Icon & Label */}
        <div className="flex items-center gap-2 flex-1">
          <span className="text-orange-400 text-sm font-semibold">
            {typeInfo?.label || widget.type}
          </span>
          {widget.id && (
            <span className="text-xs text-slate-500">#{widget.id}</span>
          )}
        </div>

        {/* Expand/Collapse */}
        <button className="text-slate-400 hover:text-slate-200">
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Delete Button */}
        <button
          className="text-slate-500 hover:text-red-400 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* BODY */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-slate-700/50">
          {/* Widget Type Selector */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-400 uppercase block">위젯 타입</label>
            <select
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 w-full"
              value={widget.type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {WIDGET_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Widget ID */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-400 uppercase block">위젯 ID</label>
            <input
              type="text"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 w-full"
              value={widget.id || ''}
              onChange={(e) => {
                const newWidget = { ...widget };
                if (e.target.value) {
                  newWidget.id = e.target.value;
                } else {
                  delete newWidget.id;
                  delete newWidget.hidden;
                }
                onChange(newWidget);
              }}
              placeholder="확장 타겟용 ID (예: risk_detail)"
            />
            <p className="text-xs text-slate-500 mt-0.5">expand 액션의 대상으로 사용됩니다</p>
          </div>

          {/* Hidden Toggle — always visible when ID is set */}
          {widget.id && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-400 uppercase">숨김 위젯</label>
              <button
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  widget.hidden ? 'bg-orange-500' : 'bg-slate-600'
                }`}
                onClick={() => onChange({ ...widget, hidden: !widget.hidden })}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    widget.hidden ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-xs text-slate-500">expand 시 표시됨</span>
            </div>
          )}

          {/* Options Fields */}
          {schema.optionFields.map((field) =>
            renderField(field, editableOptions[field.key], handleOptionChange)
          )}

          {/* Items Array */}
          {schema.hasItems && (
            <div className="space-y-2 pt-2 border-t border-slate-700/50">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-300">항목 목록</h4>
                <button
                  className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors"
                  onClick={handleAddItem}
                >
                  + 추가
                </button>
              </div>
              {(widget.items || []).map((item, itemIdx) => (
                <div key={itemIdx} className="bg-slate-900/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">항목 #{itemIdx + 1}</span>
                    <button
                      className="text-xs text-red-400 hover:text-red-300"
                      onClick={() => handleRemoveItem(itemIdx)}
                    >
                      삭제
                    </button>
                  </div>
                   {schema.itemFields.map((field) =>
                      renderField(field, item[field.key], (key, value) => handleItemChange(itemIdx, key, value), { ...item, _itemIdx: itemIdx, _parentOptions: editableOptions })
                    )}
                    {/* Item-level colorRules (metric_grid) */}
                    {schema.hasColorRules && schema.colorRulesTarget === 'items' && (
                      renderColorRulesEditor(
                        item.colorRules,
                        schema.colorRuleFields,
                        (ruleIdx, key, value) => handleItemColorRuleChange(itemIdx, ruleIdx, key, value),
                        () => handleAddItemColorRule(itemIdx),
                        (ruleIdx) => handleRemoveItemColorRule(itemIdx, ruleIdx),
                        `조건부 색상 (항목 #${itemIdx + 1})`
                      )
                    )}
                    {/* Item-level onClick (metric_grid update action) */}
                    {schema.hasOnClick && (
                      <div className="space-y-1.5 pt-2 mt-2 border-t border-slate-700/30">
                        <span className="text-[10px] text-slate-500 font-bold uppercase">클릭 액션</span>
                        {schema.onClickFields.map((field) => {
                          // Check condition with onClick context
                          if (field.condition && !field.condition(item.onClick || {})) return null;
                          return renderField(
                            field,
                            (item.onClick || {})[field.key],
                            (key, value) => handleItemOnClickChange(itemIdx, key, value),
                            item.onClick || {}
                          );
                        })}
                      </div>
                    )}
                 </div>
              ))}
            </div>
          )}

          {/* Columns Array (data_table only) */}
          {schema.hasColumns && (
            <div className="space-y-2 pt-2 border-t border-slate-700/50">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-300">컬럼 설정</h4>
                <button
                  className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors"
                  onClick={handleAddColumn}
                >
                  + 추가
                </button>
              </div>
              {(editableOptions.columns || []).map((col, colIdx) => (
                <div key={colIdx} className="bg-slate-900/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">컬럼 #{colIdx + 1}</span>
                    <button
                      className="text-xs text-red-400 hover:text-red-300"
                      onClick={() => handleRemoveColumn(colIdx)}
                    >
                      삭제
                    </button>
                  </div>
                  {schema.columnFields.map((field) =>
                    renderField(field, col[field.key], (key, value) => handleColumnChange(colIdx, key, value), col)
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Reference Lines (chart only) */}
          {schema.hasReferenceLines && schema.referenceLineCondition(editableOptions) && (
            <div className="space-y-2 pt-2 border-t border-slate-700/50">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-300">기준선</h4>
                <button
                  className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors"
                  onClick={handleAddReferenceLine}
                >
                  + 추가
                </button>
              </div>
              {(editableOptions.referenceLines || []).map((line, lineIdx) => (
                <div key={lineIdx} className="bg-slate-900/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">기준선 #{lineIdx + 1}</span>
                    <button
                      className="text-xs text-red-400 hover:text-red-300"
                      onClick={() => handleRemoveReferenceLine(lineIdx)}
                    >
                      삭제
                    </button>
                  </div>
                  {schema.referenceLineFields.map((field) =>
                    renderField(field, line[field.key], (key, value) => handleReferenceLineChange(lineIdx, key, value), line)
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Options-level Color Rules (progress_bar) */}
          {schema.hasColorRules && schema.colorRulesTarget === 'options' && (
            <div className="space-y-2 pt-2 border-t border-slate-700/50">
              {renderColorRulesEditor(
                editableOptions.colorRules,
                schema.colorRuleFields,
                handleOptionColorRuleChange,
                handleAddOptionColorRule,
                handleRemoveOptionColorRule,
                "조건부 색상 규칙"
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
